import { Params } from '@angular/router';
import { IgMedia } from '@core/models/ApiFields/ig-entities';
import { InsChartOptsFormatter } from '@features/social-analysis/components/chart/utils/chart-opts-format';
import { Paginated, IData, RawDataWithBreakd } from '@core/models/IApi';
import { Dimension } from '@features/social-analysis/models/IDimensions';
import { IG_ENDPOINT } from '@features/social-analysis/social-listening/config/api-params';
import { SocListnInsChart } from '@features/social-analysis/social-shared/store/chart.store';
import { PageFiltersState } from '@features/social-analysis/social-shared/store/page-filters.store';
import { DATA_STATUS } from '@features/social-analysis/social-shared/store/state.store';
import { ChartHttpBuilder } from './chart.http';

export class InsightsChartHttpBuilder extends ChartHttpBuilder<SocListnInsChart> {
    protected formatter = new InsChartOptsFormatter();

    reqData(state: SocListnInsChart, pageFilters: PageFiltersState): void {
        const url = this.buildUrl(IG_ENDPOINT);
        const params = this.buildParams(state, pageFilters);

        state.isLoading.set(true);
        this.setLoadingOpts(state);

        this.makeGetReq(url, params, state);
    }

    protected buildParams(
        { dimensions, metrics }: SocListnInsChart,
        pageFilters: PageFiltersState
    ): Params {
        return {
            ...this.getDates(pageFilters.selectedDates()),
            ...this.getFields(dimensions(), metrics()),
            ...this.getSort(dimensions()),
            ...this.getMode(),
            ...this.getMediaTypeFilter(),
            ...this.getFilters(pageFilters.appliedFilters()),
        };
    }

    private getMediaTypeFilter() {
        return { [IgMedia.getPrefixed('mediaType')]: 'neq:tag' };
    }

    protected override getFields(dims: Dimension[], metrics: Dimension[], params?: Params) {
        const [dimFields, breakdowns] = this.splitDims(dims);
        const metricStr = metrics.map(met => met.value).join();

        return { ...params, breakdowns: breakdowns, fields: dimFields + ',' + metricStr };
    }

    protected override formatRes(res: Paginated<RawDataWithBreakd[]>) {
        const formatted = res.data.map(data => {
            const fixedDim = this.flattenOneLevel(data.breakdown);
            const datapoints = data.data.map(dpoint => ({
                ...this.flattenOneLevel(dpoint),
                ...fixedDim,
            }));
            return datapoints;
        });

        return formatted;
    }

    protected setLoadingOpts(state: SocListnInsChart) {
        const opts = this.formatter.getLoadingChart();
        state.computedData.set(opts);
    }

    override handleSuccess(state: any, data: IData[][]): void {
        state.data.set(data);
        state.dataStatus.set(DATA_STATUS.OK);
        this.setChartOpts(state, data);
    }

    protected setChartOpts(state: SocListnInsChart, data: IData[][]) {
        const opts = this.formatter.getChartOpts(state, data);
        state.computedData.set(opts);
        state.series.set(this.getSeriesNames(opts.series));
    }

    protected getSeriesNames(series: { name: string }[]) {
        return series.map(s => ({ display: s.name, visible: true }));
    }
}
